<template>
    <div class="large-text-editor" :class="{ 'wm-toastui-dark': isDark }">
        <div class="text-h6 d-flex justify-space-between align-center">
            <span>{{ label }}</span>
            <span class="text-caption font-weight-regular" :class="{ 'error--text': isOverLimit }">
                {{ charCount.toLocaleString() }} / {{ maxLength.toLocaleString() }}
            </span>
        </div>
        <editor
            ref="toastEditor"
            initial-edit-type="wysiwyg"
            preview-style="tab"
            :initial-value="value || ''"
            :height="editorHeight"
            :options="{ minHeight: '200px', language: 'en-GB' }"
            @load="handleLoad"
            @change="handleChange"
        />
        <!-- Carries the length limit into the surrounding v-form so an over-length body blocks
             Save, and surfaces the error message. Renders no control of its own. -->
        <v-input :value="value" :rules="lengthRules" hide-details="auto" class="pt-1 mt-0" />
    </div>
</template>
<script>
import 'codemirror/lib/codemirror.css'
import '@toast-ui/editor/dist/toastui-editor.css'
import '@/assets/toastui-editor-dark.css'
import { Editor } from '@toast-ui/vue-editor'
import { stripPresentationalMarkup } from '@/utils/stripPresentationalMarkup'

export default {
    components: { Editor },
    computed: {
        isDark() {
            return this.$vuetify.theme.dark
        },
        charCount() {
            return (this.value || '').length
        },
        isOverLimit() {
            return this.charCount > this.maxLength
        },
        lengthRules() {
            // Counts the stored markdown (what goes to the DB), not the rendered text.
            return [() => !this.isOverLimit || `Maximum ${this.maxLength.toLocaleString()} characters`]
        },
    },
    props: {
        value: {
            type: String,
            // Not required + default '': a new entity's description arrives as null, and Vue only
            // skips the String type-check for a null value when the prop is optional. This clears
            // the "Invalid prop: Expected String, got Null" warning on new forms.
            default: '',
        },
        label: {
            type: String,
            required: true,
        },
        editorHeight: {
            type: String,
            default: '500px',
        },
        // System-wide character ceiling for every rich-text field. The backing columns are
        // MEDIUMTEXT (16 MB), so this is a UX guardrail well below any posting/retrieval limit,
        // not a storage constraint. Overridable per field if one ever needs a tighter cap.
        maxLength: {
            type: Number,
            default: 100000,
        },
    },
    created() {
        // Toast UI re-serialises markdown when it loads (normalises CRLF -> LF, escapes literal
        // markup, wraps HTML), so getMarkdown() !== value even when the user has changed nothing.
        // The baseline (set on @load) is the editor's own normalised rendering of the current
        // value; changes are only emitted once they diverge from it, so opening a form no longer
        // marks it dirty. Until load, _editorReady is false and every change is ignored.
        //
        // These flags are set here, not in mounted(): the child <editor> mounts (and can fire its
        // synchronous @load) before this component's mounted() hook runs, so initialising them
        // later would clobber a baseline that handleLoad already captured.
        this._editorReady = false
        this._normalizedValue = this.value || ''
        // Guards handleChange while we push a value in programmatically (reload/reset), so the
        // resulting change event isn't mistaken for a user edit.
        this._suppressChange = false
    },
    mounted() {
        // Toast UI v2 doesn't populate CodeMirror on mode switch from WYSIWYG.
        // Force-sync content then refresh so CodeMirror activates its scrollbar
        // and correctly computes cursor/selection positions.
        this._onChangeMode = mode => {
            if (mode === 'markdown') {
                this.$nextTick(() => {
                    const md = this.$refs.toastEditor.invoke('getMarkdown')
                    this.$refs.toastEditor.invoke('setMarkdown', md)
                    this.$refs.toastEditor.invoke('getCodeMirror').refresh()
                })
            }
        }
        this.$refs.toastEditor.invoke('on', 'changeMode', this._onChangeMode)

        // Squire hands us the pasted DocumentFragment before it is inserted, and mutations
        // here are what actually get pasted. Strip presentational markup so it never reaches
        // getMarkdown() and, from there, the database.
        //
        // Toast UI's own event manager ('on'/'off', 'willPaste') does deliver this event
        // reliably: WysiwygEditor re-emits Squire's event through it wrapped as
        // { source: 'wysiwyg', data: ev }, so the fragment lives at event.data.fragment,
        // not event.fragment. We bind to Squire directly via getSquire() instead so we
        // receive Squire's own unwrapped event. getSquire() is an undocumented internal
        // API, not part of Toast UI's public contract, so the try/catch here isn't
        // guarding a normal runtime condition (Squire is constructed unconditionally, so
        // this shouldn't throw today) — it's protecting against a future toastui-editor
        // upgrade renaming/removing getSquire() or changing Squire's willPaste internals.
        // If that ever happens we log rather than fail silently, since a swallowed
        // exception here would otherwise mean paste sanitisation stops working with zero
        // signal. A maintainer hitting that can switch to the public route by invoking
        // 'on'/'willPaste' and reading event.data.fragment instead of event.fragment.
        this._onWillPaste = event => {
            if (event && event.fragment) stripPresentationalMarkup(event.fragment)
        }
        try {
            this.$refs.toastEditor.invoke('getSquire').addEventListener('willPaste', this._onWillPaste)
        } catch (e) {
            console.warn('LargeTextEditor: could not bind to Squire willPaste; paste sanitisation is not active.', e)
        }
    },
    beforeDestroy() {
        this.$refs.toastEditor.invoke('off', 'changeMode', this._onChangeMode)
        try {
            this.$refs.toastEditor.invoke('getSquire').removeEventListener('willPaste', this._onWillPaste)
        } catch (e) {
            console.warn('LargeTextEditor: could not unbind Squire willPaste listener.', e)
        }
    },
    watch: {
        value(newValue) {
            if (!this.$refs.toastEditor) return
            const current = this.$refs.toastEditor.invoke('getMarkdown')
            if (newValue !== current) {
                // Programmatic value change (doc reload / reset), not a user edit. Suppress the
                // change it triggers and re-baseline to the editor's normalised rendering.
                this._suppressChange = true
                this.$refs.toastEditor.invoke('setMarkdown', newValue || '')
                this._normalizedValue = this.$refs.toastEditor.invoke('getMarkdown')
                this._suppressChange = false
            }
        },
    },
    methods: {
        handleLoad(editorInstance) {
            // Use the instance handed to the load event: @load can fire while the child is still
            // mounting, before this.$refs.toastEditor is wired up. Fall back to the ref for safety.
            const editor = editorInstance || this.$refs.toastEditor
            this._normalizedValue = editor.getMarkdown ? editor.getMarkdown() : editor.invoke('getMarkdown')
            this._editorReady = true
        },
        handleChange() {
            if (!this._editorReady || this._suppressChange) return
            const markdown = this.$refs.toastEditor.invoke('getMarkdown')
            // Compare against the editor's own normalised baseline, not the raw prop: the init
            // round-trip and mode switches change formatting without changing meaning.
            if (markdown === this._normalizedValue) return
            this._normalizedValue = markdown
            this.$emit('input', markdown)
        },
    },
}
</script>
